import React, { useState, useEffect, useContext } from "react";
import styles from "../style/pages/PublicAssetPage.module.css";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPublicAssetDetails } from "../services/publicAPIService";
import { getFullImageUrl, getThumbnailUrl } from "../utils/urlConfig";
import {
  FaBox,
  FaBarcode,
  FaHistory,
  FaShieldAlt,
  FaShoppingCart,
  FaTag,
  FaChevronRight,
  FaDownload,
  FaBoxOpen,
  FaTools,
  FaImage
} from "react-icons/fa";
import QRCode from "react-qr-code";
import { AuthContext } from "../context/AuthContext";
import ActiveScanService from "../services/ActiveScanService";

const PublicAssetPage = () => {
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [imageError, setImageError] = useState(false);

  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        const response = await fetchPublicAssetDetails(assetId);
        setAsset(response);
        if (response.images && response.images.length > 0) {
          setActiveImage(response.images[0]);
        }
      } catch (error) {
        setError("Asset not found or private.");
      } finally {
        setLoading(false);
      }
    };
    fetchAsset();
  }, [assetId]);

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.spinner}></div>
        <p>Fetching asset details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrapper}>
        <FaBoxOpen size={48} />
        <h2>Asset Not Found</h2>
        <p>{error}</p>
      </div>
    );
  }

  const renderBreadcrumbs = () => {
    const crumbs = [];

    // Container Hierarchy
    let currentLoc = asset.location;
    const locCrumbs = [];
    while (currentLoc) {
      locCrumbs.unshift(currentLoc.name);
      currentLoc = currentLoc.parentLocation;
    }
    crumbs.push(...locCrumbs);

    // Asset Hierarchy
    if (asset.parentAsset) {
      crumbs.push(asset.parentAsset.name);
    }

    if (crumbs.length === 0) return null;

    return (
      <div className={styles.breadcrumbs}>
        {crumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            <span>{crumb}</span>
            {index < crumbs.length - 1 && <FaChevronRight className={styles.breadcrumbSeparator} />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>

        {/* Main Content Column */}
        <div className={styles.mainContent}>

          {/* Header Card */}
          <div className={styles.card}>
            <div className={styles.mainGallery}>
              <div className={styles.imageWrapper}>
                {activeImage && !imageError ? (
                  <img
                    src={getThumbnailUrl(activeImage)}
                    alt={asset.name}
                    className={styles.assetImage}
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      // Fallback to full-size image if thumbnail is missing on disk
                      if (e.target.src !== getFullImageUrl(activeImage)) {
                        e.target.src = getFullImageUrl(activeImage);
                        return;
                      }
                      setImageError(true);
                    }}
                  />
                ) : (
                  <div className={styles.noImagePlaceholder}>
                    <FaBoxOpen size={64} color="#CBD5E1" />
                    <span style={{ marginTop: '8px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      {imageError ? "Image unavailable" : "No image uploaded"}
                    </span>
                  </div>
                )}
              </div>

              {asset.images?.length > 1 && (
                <div className={styles.thumbnailGallery}>
                  {asset.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={getThumbnailUrl(img)}
                      className={`${styles.thumbnail} ${activeImage === img ? styles.activeThumbnail : ""}`}
                      onClick={() => {
                        setActiveImage(img);
                        setImageError(false);
                      }}
                      alt={`View ${idx + 1}`}
                      loading="lazy"
                      onError={(e) => {
                        // Fallback to full image if thumbnail is missing
                        if (e.target.src !== getFullImageUrl(img)) {
                          e.target.src = getFullImageUrl(img);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={styles.assetHeaderInfo}>
              {renderBreadcrumbs()}
              <h1 className={styles.assetName}>{asset.name}</h1>
              <div className={`${styles.assetBadge} ${asset.damaged ? styles.damagedBadge : ""}`}>
                {asset.damaged ? "Damaged / Issues" : "In Good Condition"}
              </div>
              <p className={styles.description}>{asset.description || "No description provided."}</p>
            </div>
          </div>

          {/* Details Sections */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}><FaTag /> Identification</div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.metaGrid}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{asset.qrToken ? "Label ID" : "System ID"}</span>
                  <span className={styles.metaValue}>{asset.qrToken ? asset.qrToken : `#${assetId}`}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Manufacturer</span>
                  <span className={styles.metaValue}>{asset.manufacturer || "N/A"}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Model No</span>
                  <span className={styles.metaValue}>{asset.modelNo || "N/A"}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Serial No</span>
                  <span className={styles.metaValue}>{asset.serialNo || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Purchase Details */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}><FaShoppingCart /> Purchase Info</div>
              </div>
              <div className={styles.cardContent}>
                {asset.purchasedDetails ? (
                  <div className={styles.list}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Store</span>
                      <span className={styles.metaValue}>{asset.purchasedDetails.storeName || "Unknown"}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Price</span>
                      <span className={styles.metaValue}>
                        {asset.purchasedDetails.purchasedPrice ? `$${asset.purchasedDetails.purchasedPrice}` : "N/A"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Date</span>
                      <span className={styles.metaValue}>
                        {asset.purchasedDetails.purchasedDate ? new Date(asset.purchasedDetails.purchasedDate).toLocaleDateString() : "N/A"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className={styles.noData}>No purchase information available.</p>
                )}
              </div>
            </div>

            {/* Warranty & Insurance */}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}><FaShieldAlt /> Protection</div>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.list}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Insured</span>
                    <span className={styles.metaValue}>{asset.insured ? "Yes" : "No"}</span>
                  </div>
                  {asset.warrantyDetails && (
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Warranty Expires</span>
                      <span className={styles.metaValue}>
                        {asset.warrantyDetails.warrantyExpiresOn
                          ? new Date(asset.warrantyDetails.warrantyExpiresOn).toLocaleDateString()
                          : "Lifetime"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Maintenance History */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}><FaHistory /> Maintenance History</div>
            </div>
            <div className={styles.cardContent}>
              {asset.maintenanceRecords?.length > 0 ? (
                <div className={styles.list}>
                  {asset.maintenanceRecords.map((m, idx) => (
                    <div key={idx} className={styles.listItem}>
                      <div className={styles.listItemIcon}><FaTools /></div>
                      <div className={styles.listItemContent}>
                        <div className={styles.listItemTitle}>
                          {m.isBattery ? "Battery Replacement" : "General Service"}
                        </div>
                        <div className={styles.listItemSubtitle}>
                          Completed: {new Date(m.completedDate).toLocaleDateString()}
                        </div>
                      </div>
                      {m.cost > 0 && <div className={styles.metaValue}>${m.cost}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noData}>No maintenance records found.</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>

          {/* QR Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}><FaBarcode /> Asset Label</div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.qrContainer}>
                {asset.qrToken ? (
                  <div style={{ background: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                    <QRCode 
                      value={`${window.location.origin}/scan/${asset.qrToken}`} 
                      size={150} 
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                    <div style={{ fontSize: '12px', fontWeight: 'bold', marginTop: '8px', color: '#64748b' }}>
                      {asset.qrToken}
                    </div>
                  </div>
                ) : asset.qr_code ? (
                  <>
                    <img
                      src={getFullImageUrl(asset.qr_code)}
                      alt="QR Code"
                      className={styles.qrImage}
                    />
                    <a href={getFullImageUrl(asset.qr_code)} download className={styles.downloadLink}>
                      <FaDownload /> Download Label
                    </a>
                  </>
                ) : (
                  <p className={styles.noData}>Label not generated.</p>
                )}
                {user && (
                    <button 
                      onClick={async () => {
                        const activeScan = await ActiveScanService.getActiveScan();
                        let stateToSave = {
                          firstItemId: asset.id,
                          firstItemType: 'asset',
                          secondItemId: -1,
                          secondItemType: ''
                        };

                        if (activeScan && activeScan.firstItemId !== undefined && activeScan.firstItemId !== -1) {
                          if (!(activeScan.firstItemType === 'asset' && Number(activeScan.firstItemId) === Number(asset.id))) {
                             stateToSave = {
                               firstItemId: activeScan.firstItemId,
                               firstItemType: activeScan.firstItemType,
                               secondItemId: asset.id,
                               secondItemType: 'asset'
                             };
                          }
                        }

                        await ActiveScanService.saveActiveScan(stateToSave);
                        navigate('/admin?page=scanner');
                      }}
                      style={{ marginTop: '15px', width: '100%', padding: '10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <FaBarcode style={{ marginRight: '8px' }} /> Open in Scanner
                    </button>
                )}
              </div>
            </div>
          </div>

          {/* Containment Info */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}><FaBox /> Sub-Assets</div>
            </div>
            <div className={styles.cardContent}>
              {asset.childAssets?.length > 0 ? (
                <div className={styles.list}>
                  {asset.childAssets.map((child, idx) => (
                    <div key={idx} className={styles.listItem} style={{ cursor: 'pointer' }} onClick={() => window.location.href = `/public/assets/${child.qrToken || child.id}`}>
                      <div className={styles.listItemIcon}>
                        {child.image ? (
                          <img src={getThumbnailUrl(child.image)} className={styles.thumbnail} style={{ width: '100%', height: '100%' }} alt={child.name} loading="lazy" />
                        ) : <FaBox />}
                      </div>
                      <div className={styles.listItemContent}>
                        <div className={styles.listItemTitle}>{child.name}</div>
                        <div className={styles.listItemSubtitle}>ID: {child.qrToken || `#${child.id}`}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.noData}>No nested assets.</p>
              )}
            </div>
          </div>

          {/* Notes Card */}
          {asset.notes && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Notes</div>
              </div>
              <div className={styles.cardContent}>
                <p className={styles.metaValue} style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                  {asset.notes}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PublicAssetPage;
